<template>
  <div class="relative">
    <input
      :id="id"
      :type="isPasswordVisible ? 'text' : 'password'"
      class="base-input"
      v-model="model"
      :class="[inputSizeClass, borderClass, readonlyInputClass]"
      :readonly="isReadonly"
      :autocomplete="isReadonly ? 'off' : 'on'"
      required
      @blur="isPasswordHelper = false"
      @focus="isPasswordHelper = true"
      ref="password_input"
    />
    <label :for="id" :class="readonlyLabelClass" class="material-input-label input-position">{{
      placeholder
    }}</label>
    <eye-icon
      v-if="isPasswordVisible"
      class="eye-icon eye-position"
      @click.native="isPasswordVisible = false"
    />
    <eye-slash-icon
      v-if="!isPasswordVisible && !isReadonly"
      class="eye-icon eye-position"
      @click.native="isPasswordVisible = true"
    />
    <div dir="ltr">
      <div class="bar" :class="strengthClass" v-if="shouldValidate && model.length"></div>
    </div>
    <div v-if="shouldValidate && model.length && isPasswordHelper" class="helper-wrapper">
      <div class="flex flex-col px-20 py-10">
        <div class="helper-section">
          <tick-icon v-if="isSevenCharacter" />
          <p class="text-12" :class="{ 'text-success': isSevenCharacter }">
            {{ $t('at_least_8_characters') }}
          </p>
        </div>
        <div class="helper-section">
          <tick-icon v-if="isLowercaseUppercase" />
          <p class="text-12" :class="{ 'text-success': isLowercaseUppercase }">
            {{ $t('include_lower_uppercase') }}
          </p>
        </div>
        <div class="helper-section">
          <tick-icon v-if="isNumber" />
          <p class="text-12" :class="{ 'text-success': isNumber }">
            {{ $t('include_numbers') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TickIcon from '@/assets/icons/vue/Tick.vue';
import EyeIcon from '../../assets/icons/vue/eye.vue';
import EyeSlashIcon from '../../assets/icons/vue/eye-slash.vue';

export default {
  name: 'BasePasswordMeter',
  components: {
    EyeSlashIcon,
    EyeIcon,
    TickIcon,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      default: '',
      required: true,
    },
    radius: {
      type: String,
      default: 'sm',
    },
    width: {
      type: String,
      default: '318',
    },
    height: {
      type: String,
      default: '50',
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
    shouldValidate: {
      type: Boolean,
      default: false,
    },
    shouldFocus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPasswordVisible: false,
      passwordStrength: '',
      isPasswordHelper: true,
      isNumber: false,
      isSevenCharacter: false,
      isLowercaseUppercase: false,
    };
  },
  mounted() {
    if (this.shouldFocus) {
      this.$refs.password_input.focus();
    }
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
        this.passwordStrengthHandler(value);
        this.passwordHelperHandler(value);
      },
    },
    inputSizeClass() {
      return `w-${this.width} h-${this.height}`;
    },
    borderClass() {
      return {
        rounded: this.radius === 'default',

        'rounded-sm': this.radius === 'sm',
      };
    },
    strengthClass() {
      if (this.passwordStrength === 'strong') return 'strong';
      if (this.passwordStrength === 'mid') return 'mid';
      return 'weak';
    },
    inputType() {
      return this.isPasswordVisible ? 'password' : 'text';
    },
    readonlyLabelClass() {
      return this.isReadonly ? 'readonly' : '';
    },
    readonlyInputClass() {
      return this.isReadonly ? 'cursor-pointer' : '';
    },
  },
  methods: {
    passwordStrengthHandler(value) {
      const MID_PASSWORD = /^\S*(?=\S{8,})(?=\S*[a-z])(?=\S*[A-Z])\S*$/.test(value);
      const STRONG_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*,;]).{8,}$/.test(value);
      if (STRONG_PASSWORD) {
        this.passwordStrength = 'strong';
      } else if (MID_PASSWORD) {
        this.passwordStrength = 'mid';
      } else {
        this.passwordStrength = 'weak';
      }
    },
    passwordHelperHandler(value) {
      this.isNumber = /\d/.test(value);
      this.isLowercaseUppercase = /[a-z].*[A-Z]|[A-Z].*[a-z]/.test(value);
      this.isSevenCharacter = value.length >= 8;
    },
  },
};
</script>

<style scoped>
.bar {
  @apply absolute text-transparent rounded-full transform translate-x-10 -translate-y-3;
  height: 4px;
  border: 1px;
  transition: all 0.4s;
  -webkit-transition: all 0.4s;
  -moz-transition: all 0.4s;
}
.weak {
  @apply bg-danger;
  width: 20%;
}
.mid {
  @apply bg-warning;
  width: 50%;
}
.strong {
  @apply bg-success w-294;
}
.base-input {
  @apply block py-10 text-12 border-default
  text-title relative font-semibold bg-theme-secondary disabled:text-disabled;
}

.eye-icon {
  @apply absolute top-12 cursor-pointer;
}

input:focus ~ .material-input-label,
input:valid ~ .material-input-label {
  @apply text-12 -top-15 px-2;
}
.readonly {
  top: -10px !important;
  padding: 0 0.5rem !important;
  font-size: 12px !important;
}
.material-input-label {
  @apply font-normal absolute px-5 py-5 mx-5 text-title
  transition-all bg-theme-secondary ease-in-out duration-300 top-1.5;
  pointer-events: none;
}
input[type='password'] {
  letter-spacing: 0.3em;
  outline: none;
}
.helper-section {
  @apply flex items-center pb-10 gap-x-3;
}
.helper-wrapper {
  @apply w-2/3 bg-secondary absolute top-60 right-0 z-10 rounded;
}
.input-position {
  @apply left-3.5 right-auto;
}
.eye-position {
  @apply right-10 left-auto;
}
.eye-position:is(:lang(fa), :lang(ar)) {
  @apply left-10 right-auto;
}

.input-position:is(:lang(fa), :lang(ar)) {
  @apply right-3.5 left-auto;
}
</style>
